import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import districts from "../../data/district.json";
import upazilas from "../../data/upazila.json";
import donorPlaceholder from "../../assets/image/noimage.gif";
import { Link } from "react-router-dom";
import { Modal } from "react-responsive-modal";
import QRCode from "react-qr-code";
import { jsPDF } from "jspdf";
import * as htmlToImage from "html-to-image";
import {
  User,
  Droplet,
  Phone,
  MapPin,
  Search,
  Download,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { baseUrl } from "../../utils/url";
import useLogo from "../../hooks/useLogo";

const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const BloodDonor = () => {
  const [donors, setDonors] = useState([]);
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("");
  const [upazila, setUpazila] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [bloodGroup, setBloodGroup] = useState("");
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'card'
  const [donorImageBase64, setDonorImageBase64] = useState("");
  const { data: logoUrl } = useLogo();

  const convertImageToBase64 = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL("image/jpeg", 0.8);
          resolve(dataURL);
        } catch (error) {
          resolve(donorPlaceholder); // fallback to placeholder
        }
      };
      img.onerror = () => {
        resolve(donorPlaceholder); // fallback to placeholder
      };
      img.src = imageUrl;
    });
  };

  // Function to get district name by ID
  const getDistrictById = (id) => {
    const district = districts.find((district) => district.id === id);
    return district ? district.bn_name : null;
  };

  // Function to get upazila name by ID
  const getUpazilaById = (id) => {
    const upazila = upazilas.find((upazila) => upazila.id === id);
    return upazila ? upazila.bn_name : null;
  };


  const fetchDonors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(baseUrl + "blood-donor", {
        params: {
          search,
          district: getDistrictById(district),
          upazila: getUpazilaById(upazila),
          bloodGroup,
          page,
          limit: 10,
        },
      });
      setDonors(response.data.data);
      setTotalPages(Math.ceil(response.data.total / 10));
    } catch (error) {
      // Error fetching donors - handled silently
    } finally {
      setLoading(false);
    }
  }, [search, district, upazila, bloodGroup, page]);

  useEffect(() => {
    fetchDonors();
  }, [search, district, upazila, page, bloodGroup, fetchDonors]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when search changes
  };

  const handleDistrictChange = (e) => {
    setDistrict(e.target.value);
    setUpazila(""); // reset upazila when district changes
    setPage(1); // Reset to first page when filter changes
  };

  const handleUpazilaChange = (e) => {
    setUpazila(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handleBloodGroupChange = (e) => {
    setBloodGroup(e.target.value);
    setPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const openModal = async (donor) => {
    setSelectedDonor(donor);
    setModalIsOpen(true);

    // Convert image to base64 when modal opens
    const imageUrl = donor.profileImage
      ? `${baseUrl}${donor.profileImage}`
      : donorPlaceholder;

    const base64Image = await convertImageToBase64(imageUrl);
    setDonorImageBase64(base64Image);
  };

  const closeModal = () => {
    setSelectedDonor(null);
    setModalIsOpen(false);
  };

  const handlePrintDocument = async () => {
    const pdfWidth = 148;
    const pdfHeight = 210;

    const idCard = document.getElementById("idCard");
    const downloadButton = document.getElementById("downloadButton");

    if (!idCard || !downloadButton) {
      return;
    }

    try {
      const originalCardStyle = {
        width: idCard.style.width,
        height: idCard.style.height,
      };
      const originalButtonDisplay = downloadButton.style.display;

      idCard.style.width = `${pdfWidth}mm`;
      idCard.style.height = `${pdfHeight}mm`;
      downloadButton.style.display = "none";

      await new Promise((resolve) => setTimeout(resolve, 200));

      const dataUrl = await htmlToImage.toPng(idCard, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const pdf = new jsPDF({
        unit: "mm",
        format: [pdfWidth, pdfHeight],
        compress: true,
      });

      const imgProps = pdf.getImageProperties(dataUrl);
      const aspectRatio = imgProps.width / imgProps.height;

      const imgWidth = pdfWidth;
      const imgHeight = imgWidth / aspectRatio;

      pdf.addImage(dataUrl, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`${selectedDonor.name}-id-card.pdf`);

      idCard.style.width = originalCardStyle.width;
      idCard.style.height = originalCardStyle.height;
      downloadButton.style.display = originalButtonDisplay;
    } catch (error) {
      alert("Failed to generate PDF. Please try again.");

      idCard.style.width = "";
      idCard.style.height = "";
      downloadButton.style.display = "";
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Blood Donors</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <button
            onClick={() => setViewMode(viewMode === "table" ? "card" : "table")}
            className="btn btn-outline"
          >
            {viewMode === "table" ? "Card View" : "Table View"}
          </button>
          <Link
            className="btn bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            to={"/blood-donor-register"}
          >
            <PlusCircle size={18} />
            <span>ব্লাড ডোনার নিবন্ধন</span>
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="text-gray-400" size={18} />
            </div>
            <input
              type="text"
              placeholder="Search by name or mobile"
              onChange={handleSearch}
              value={search}
              className="input input-bordered w-full pl-10"
            />
          </div>

          <select
            onChange={handleBloodGroupChange}
            value={bloodGroup}
            className="select select-bordered w-full"
          >
            <option value="">All Blood Groups</option>
            {bloodGroups.map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>

          <select
            value={district}
            onChange={handleDistrictChange}
            className="select select-bordered w-full"
          >
            <option value="">All Districts</option>
            {districts.map((dist) => (
              <option key={dist.id} value={dist.id}>
                {dist.bn_name}
              </option>
            ))}
          </select>

          <select
            value={upazila}
            onChange={handleUpazilaChange}
            disabled={!district}
            className="select select-bordered w-full"
          >
            <option value="">All Upazilas</option>
            {upazilas
              .filter((upa) => upa.district_id === district)
              .map((upa) => (
                <option key={upa.id} value={upa.id}>
                  {upa.bn_name}
                </option>
              ))}
          </select>

        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      )}

      {/* Empty State */}
      {!loading && donors.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <User className="text-gray-400" size={40} />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No donors found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search or filter to find what you're looking for.
          </p>
        </div>
      )}

      {/* Table View */}
      {!loading && donors.length > 0 && viewMode === "table" && (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Donor
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Blood Group
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Contact
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Location
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {donors.map((donor) => (
                <tr key={donor._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          src={`${baseUrl}${donor.profileImage}`}
                          onError={(e) => (e.target.src = donorPlaceholder)}
                          alt={donor.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {donor.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Droplet
                        className={`mr-2 ${
                          donor.bloodGroup.includes("+")
                            ? "text-red-500"
                            : "text-red-400"
                        }`}
                        size={16}
                      />
                      <span className="font-bold text-red-600">
                        {donor.bloodGroup}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Phone className="mr-2 text-gray-500" size={16} />
                      <span className="text-sm text-gray-900">
                        {donor.mobile}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin className="mr-2 text-gray-500" size={16} />
                      <div className="text-sm text-gray-900">
                        <div>{donor.district}</div>
                        <div className="text-xs text-gray-500">
                          {donor.upazila}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openModal(donor)}
                      className="text-red-600 hover:text-red-900"
                    >
                      View ID
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Card View */}
      {!loading && donors.length > 0 && viewMode === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {donors.map((donor) => (
            <div
              key={donor._id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
              onClick={() => openModal(donor)}
            >
              <div className="p-4 flex items-start">
                <div className="flex-shrink-0">
                  <img
                    src={`${baseUrl}/${donor.profileImage}` || donorPlaceholder}
                    onError={(e) => (e.target.src = donorPlaceholder)}
                    alt={donor.name}
                    className="h-20 w-20 rounded-full object-cover border-2 border-red-100"
                  />
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {donor.name}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs font-bold rounded-full ${
                        donor.bloodGroup.includes("+")
                          ? "bg-red-100 text-red-800"
                          : "bg-red-50 text-red-600"
                      }`}
                    >
                      {donor.bloodGroup}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-600">
                    <Phone className="mr-2" size={14} />
                    {donor.mobile}
                  </div>
                  <div className="mt-1 flex items-center text-sm text-gray-600">
                    <MapPin className="mr-2" size={14} />
                    <span className="truncate">
                      {donor.district}, {donor.upazila}
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  Last donated: {donor.lastDonationDate ? new Date(donor.lastDonationDate).toLocaleDateString('en-GB') : "N/A"}
                </span>
                <QRCode
                  size={64}
                  className="w-8 h-8"
                  value={`Mobile: ${donor?.mobile}\nBlood Group: ${donor?.bloodGroup}`}
                  viewBox={`0 0 64 64`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && donors.length > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(page - 1) * 10 + 1}</span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(page * 10, (page - 1) * 10 + donors.length)}
            </span>{" "}
            of <span className="font-medium">{totalPages * 10}</span> results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
              className="btn btn-outline btn-sm"
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="btn btn-outline btn-sm"
            >
              <ChevronLeft size={16} />
            </button>
            {[...Array(Math.min(5, totalPages)).keys()].map((num) => {
              // Show limited page numbers
              let pageNum;
              if (totalPages <= 5) {
                pageNum = num + 1;
              } else if (page <= 3) {
                pageNum = num + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + num;
              } else {
                pageNum = page - 2 + num;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`btn btn-sm ${
                    page === pageNum ? "btn-active" : "btn-outline"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && page < totalPages - 2 && (
              <span className="px-3 py-1">...</span>
            )}
            {totalPages > 5 && page < totalPages - 2 && (
              <button
                onClick={() => handlePageChange(totalPages)}
                className={`btn btn-sm ${
                  page === totalPages ? "btn-active" : "btn-outline"
                }`}
              >
                {totalPages}
              </button>
            )}
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="btn btn-outline btn-sm"
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
              className="btn btn-outline btn-sm"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Donor ID Card Modal */}
      {selectedDonor && (
        <Modal
          classNames={{
            modal: "customModal md:w-full w-[90%] max-w-2xl",
            overlay: "customOverlay",
          }}
          open={modalIsOpen}
          onClose={closeModal}
          center
        >
          <div id="idCard" className="bg-white p-6 rounded-lg shadow-xl">
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center mb-4">
                {logoUrl ? (
                  <img src={logoUrl} className="w-16 h-16 mr-3" alt="Logo" />
                ) : (
                  <img
                    src={donorPlaceholder}
                    className="w-16 h-16 mr-3"
                    alt="Logo"
                  />
                )}
                <div>
                  <h2 className="text-xl font-bold text-center">
                    নিঃস্বার্থ নবজীবন সংগঠন (NNS)
                  </h2>
                  <p className="text-sm text-gray-600 text-center">
                    খলিফা পট্টি, আন্দরকিল্লা, চট্টগ্রাম
                  </p>
                </div>
              </div>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-red-600">
                  NNS ব্লাড টিম
                </h2>
                <p className="text-sm text-gray-600">
                  (NNS এর স্বেচ্ছাসেবী উইং)
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
              <div className="flex-shrink-0">
                <img
                  src={donorImageBase64 || donorPlaceholder}
                  alt={selectedDonor.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-red-100"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold">{selectedDonor.name}</h3>
                  <span
                    className={`text-3xl font-bold px-3 py-1 rounded ${
                      selectedDonor.bloodGroup.includes("+")
                        ? "bg-red-100 text-red-700"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    {selectedDonor.bloodGroup}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Phone className="mr-2 text-gray-600" size={18} />
                    <span>{selectedDonor.mobile}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="mr-2 text-gray-600" size={18} />
                    <span>
                      {selectedDonor.district}, {selectedDonor.upazila}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6">
              <div className="text-xs text-gray-500">
                <p>Issued on: {new Date().toLocaleDateString()}</p>
              </div>
              <QRCode
                size={80}
                value={`Mobile: ${selectedDonor.mobile}\nBlood Group: ${selectedDonor.bloodGroup}`}
                className="w-20 h-20"
              />
            </div>

            <p className="mt-6 text-xs text-center text-gray-500 border-t pt-3">
              এই কার্ডটি অনলাইনে ডোনার নিবন্ধন হিসাবে সিস্টেম থেকে তৈরী কৃত এবং
              কার্ড শুধুমাত্র ব্লাড গুপের জন্য প্রস্তুত করা হয়েছে
            </p>

            <div className="mt-6 flex justify-center">
              <button
                id="downloadButton"
                onClick={handlePrintDocument}
                className="btn bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
              >
                <Download size={18} />
                Download ID Card
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BloodDonor;
